import { useEffect, useMemo, useState, useRef } from "react";
import { useNavigate, useParams, useLocation, Navigate } from "react-router-dom";
import {
  ArrowLeft,
  CalendarDays,
  Clock,
  MapPin,
  Armchair,
  Ticket,
  User,
  UserCircle2,
  AlertCircle,
  QrCode,
  Save,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import {
  getBoletosByCompra,
  getBoletosBySocio,
  actualizarAsistente,
  getComprasBySocio,
  agruparComprasPorEvento,
  hidratarComprasBoletos,
  recargarBoleto,
} from "@/data/comprasStore";
import { getCurrentSocio, fetchSocioMe } from "@/data/socioAuthStore";
import { BoletoPorConfirmar, VerificacionBoletos } from "@/screens/app/AsignarVerificacion";
import { ASIGNAR } from "@/constants/testIds";
import { formatearFechaLarga } from "@/utils/fechas";

/**
 * Pantalla "Asignar asistentes".
 *
 * Sprint 54 · Fase A: admite dos modos:
 *   - `/app/asignar/:compraId`          → una sola compra.
 *   - `/app/eventos/:eventoId/asignar`  → TODOS los boletos del evento
 *     (todas las compras confirmadas del socio para ese evento).
 */
export default function AsignarAsistentes() {
  const { compraId, eventoId } = useParams();
  const modoEvento = Boolean(eventoId);
  const navigate = useNavigate();
  const location = useLocation();
  const [socio, setSocio] = useState(() => getCurrentSocio());
  const [tick, setTick] = useState(0);

  // Ruta actual (para que el boleto sepa volver aquí) y destino de "Regresar":
  // origen inmediato desde el que se abrió (state.from) con fallback seguro a
  // Mis Boletos si se entra por URL directa sin historial válido.
  const rutaActual = modoEvento
    ? `/app/eventos/${eventoId}/asignar`
    : `/app/asignar/${compraId}`;
  const volverA = location.state?.from || "/app/mis-boletos";

  const [verif, setVerif] = useState("cargando"); // cargando | ok | error

  // Confirma el estado con el servidor antes de habilitar la edición.
  const confirmarConServidor = async () => {
    setVerif("cargando");
    const ok = await hidratarComprasBoletos();
    if (ok) setTick((t) => t + 1);
    setVerif(ok ? "ok" : "error");
  };

  useEffect(() => {
    fetchSocioMe().then((s) => {
      if (s) setSocio(s);
    });
    confirmarConServidor();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Contexto (encabezado del evento) según el modo.
  const contexto = useMemo(() => {
    if (!socio) return null;
    if (modoEvento) {
      const g = agruparComprasPorEvento(socio.id).find(
        (x) => x.eventoId === eventoId,
      );
      if (!g) return null;
      return {
        nombreEvento: g.nombreEvento,
        fechaEvento: g.fechaEvento,
        horaEvento: g.horaEvento,
        salon: g.salones.join(" · ") || "—",
        mesaLabel:
          g.mesas.length > 1
            ? `${g.mesas.length} mesas`
            : g.mesas.length === 1
              ? `Mesa ${g.mesas[0]}`
              : null,
        cantidad: g.totalBoletos,
      };
    }
    const c = getComprasBySocio(socio.id).find((x) => x.id === compraId);
    if (!c) return null;
    return {
      nombreEvento: c.nombreEvento,
      fechaEvento: c.fechaEvento,
      horaEvento: c.horaEvento,
      salon: c.salon,
      mesaLabel: c.mesa != null ? `Mesa ${c.mesa}` : null,
      cantidad: c.cantidad,
    };
  }, [socio, modoEvento, eventoId, compraId, tick]);

  const recolectarBoletos = () => {
    if (!socio) return [];
    if (modoEvento) {
      return getBoletosBySocio(socio.id)
        .filter((b) => b.eventoId === eventoId)
        .sort(
          (a, b) =>
            (a.compraId || "").localeCompare(b.compraId || "") ||
            (a.consecutivo || 0) - (b.consecutivo || 0),
        );
    }
    return getBoletosByCompra(compraId);
  };

  const [boletos, setBoletos] = useState([]);
  // `drafts` almacena el nombre editado por boleto (id → nombre).
  const [drafts, setDrafts] = useState({});
  const [saving, setSaving] = useState(false);
  const [salirDialog, setSalirDialog] = useState(null); // { to } | null
  // Boletos rechazados (409) cuyo estado actual aún no confirmó el servidor.
  const [sinConfirmar, setSinConfirmar] = useState(() => new Set());
  const [reintentando, setReintentando] = useState(() => new Set());
  const boletosInicializadosRef = useRef(new Set());

  useEffect(() => {
    if (!contexto || verif !== "ok") return;
    const nuevos = recolectarBoletos();
    setBoletos(nuevos);
    setDrafts((prev) => {
      const next = { ...prev };
      nuevos.forEach((b) => {
        const yaVisto = boletosInicializadosRef.current.has(b.id);
        const draftActual = next[b.id];
        // Un boleto utilizado no admite edición: siempre muestra lo guardado.
        if (b.estado === "VALIDADO") {
          next[b.id] = b.asistenteNombre || "";
          boletosInicializadosRef.current.add(b.id);
          return;
        }
        // Inicializa la primera vez; y repuebla si el draft sigue vacío pero el
        // backend ya trae un nombre (evita inputs vacíos tras la hidratación y
        // que "Guardar" sobrescriba nombres reales con "").
        if (!yaVisto || (!draftActual && b.asistenteNombre)) {
          next[b.id] = b.asistenteNombre || "";
        }
        if (b.asistenteNombre) boletosInicializadosRef.current.add(b.id);
        else if (!yaVisto) boletosInicializadosRef.current.add(b.id);
      });
      return next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contexto, tick, verif]);

  const nombresRepetidos = useMemo(() => {
    const norm = boletos
      .map((b) => (drafts[b.id] ?? "").trim().toLowerCase())
      .filter(Boolean);
    const counts = norm.reduce((acc, n) => {
      acc[n] = (acc[n] || 0) + 1;
      return acc;
    }, {});
    return Object.entries(counts)
      .filter(([, c]) => c > 1)
      .map(([n]) => n);
  }, [boletos, drafts]);

  const dirtyIds = useMemo(() => {
    const s = new Set();
    boletos.forEach((b) => {
      if (b.estado === "VALIDADO" || sinConfirmar.has(b.id)) return;
      const draft = (drafts[b.id] ?? "").trim();
      const original = (b.asistenteNombre || "").trim();
      if (draft !== original) s.add(b.id);
    });
    return s;
  }, [boletos, drafts, sinConfirmar]);

  const hayCambios = dirtyIds.size > 0;

  useEffect(() => {
    const handler = (e) => {
      if (!hayCambios) return;
      e.preventDefault();
      e.returnValue = "";
      return "";
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [hayCambios]);

  if (!socio) return <Navigate to="/login" replace />;
  if (verif !== "ok") {
    return (
      <VerificacionBoletos
        estado={verif}
        onReintentar={confirmarConServidor}
        onRegresar={() => navigate(volverA)}
      />
    );
  }
  if (!contexto) return <Navigate to="/app/mis-boletos" replace />;

  const totales = {
    total: boletos.length,
    asignados: boletos.filter(
      (b) => ((drafts[b.id] ?? b.asistenteNombre) || "").trim().length > 0,
    ).length,
  };
  totales.pendientes = totales.total - totales.asignados;

  const setDraftFor = (id, value) =>
    setDrafts((prev) => ({ ...prev, [id]: value }));

  const usarMiNombreEn = (id) => {
    const nombre = (socio?.nombre || "").trim();
    if (!nombre) {
      toast.error("No pudimos obtener tu nombre desde el servidor.");
      return;
    }
    setDraftFor(id, nombre);
  };

  const intentarSalir = (to, navState) => {
    if (!hayCambios) {
      navigate(to, navState ? { state: navState } : undefined);
      return;
    }
    setSalirDialog({ to, navState });
  };

  const confirmarSalir = () => {
    const to = salirDialog?.to;
    const navState = salirDialog?.navState;
    setSalirDialog(null);
    if (to) navigate(to, navState ? { state: navState } : undefined);
  };

  const cancelarSalir = () => setSalirDialog(null);

  const guardarCambios = async () => {
    if (!hayCambios || saving) return;
    setSaving(true);
    const boletosDirty = boletos.filter((b) => dirtyIds.has(b.id));
    let okCount = 0;
    const bloqueadosIds = [];
    const releidos = new Set();
    let fallos = 0;
    for (const b of boletosDirty) {
      const nombre = (drafts[b.id] ?? "").trim();
      const res = await actualizarAsistente(b.id, socio.id, { nombre });
      if (res && res.__bloqueado) {
        bloqueadosIds.push(b.id);
        if (res.boleto) releidos.add(b.id);
        continue;
      }
      if (!res) {
        fallos += 1;
        continue;
      }
      okCount += 1;
    }
    const hidratado = await hidratarComprasBoletos();
    const frescos = recolectarBoletos();
    setBoletos(frescos);
    // Rechazados: se descarta sólo su modificación y se muestra el último
    // nombre confirmado; sin confirmación del servidor quedan bloqueados.
    if (bloqueadosIds.length > 0) {
      setDrafts((prev) => {
        const next = { ...prev };
        bloqueadosIds.forEach((id) => {
          const f = frescos.find((x) => x.id === id);
          next[id] = f?.asistenteNombre || "";
        });
        return next;
      });
      const pendientes = bloqueadosIds.filter((id) => !hidratado && !releidos.has(id));
      setSinConfirmar((prev) => {
        const next = new Set(prev);
        bloqueadosIds.forEach((id) => next.delete(id));
        pendientes.forEach((id) => next.add(id));
        return next;
      });
    }
    setSaving(false);
    const bloqueados = bloqueadosIds.length;
    if (okCount > 0) {
      toast.success(
        `${okCount} ${okCount === 1 ? "boleto guardado" : "boletos guardados"}.`,
      );
    }
    if (bloqueados > 0) {
      toast.error(
        `${bloqueados} ${bloqueados === 1 ? "boleto no admite cambios" : "boletos no admiten cambios"} y no se pudieron modificar.`,
      );
    }
    if (fallos > 0) {
      toast.error("Algunos boletos no se pudieron guardar. Intenta de nuevo.");
    }
  };

  // Reintentar: sólo consulta el estado del boleto; nunca reenvía el nombre.
  const reintentarBoleto = async (id) => {
    setReintentando((prev) => new Set(prev).add(id));
    const fresco = await recargarBoleto(id);
    setReintentando((prev) => {
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
    if (!fresco) {
      toast.error("No pudimos actualizar el estado de este boleto.");
      return;
    }
    setBoletos(recolectarBoletos());
    setDrafts((prev) => ({ ...prev, [id]: fresco.asistenteNombre || "" }));
    setSinConfirmar((prev) => {
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
  };

  return (
    <div data-testid={ASIGNAR.screen} className="flex flex-col gap-6 pb-8">
      <button
        type="button"
        data-testid={ASIGNAR.btnRegresar}
        onClick={() => intentarSalir(volverA)}
        aria-label="Regresar"
        className="self-start inline-flex items-center gap-2 rounded-full pl-2 pr-4 py-2 text-sm font-medium border hairline bg-card text-foreground hover:bg-muted transition-colors"
      >
        <span className="w-7 h-7 grid place-items-center rounded-full bg-primary/10 dark:bg-accent/15 text-primary dark:text-accent">
          <ArrowLeft className="w-4 h-4" strokeWidth={2} />
        </span>
        Regresar
      </button>

      <header className="animate-fade-up">
        <span className="overline text-accent">Asistentes</span>
        <h1 className="mt-3 font-heading text-3xl sm:text-4xl tracking-tight text-foreground">
          Asignar asistentes
        </h1>
        <p className="mt-3 text-sm text-muted-foreground max-w-xl leading-relaxed">
          {modoEvento
            ? "Asigna un nombre a cada boleto de este evento. Se incluyen todas tus compras confirmadas."
            : "Asigna un nombre a cada boleto antes del evento. Los boletos vacíos quedarán como pendientes."}
        </p>
      </header>

      {/* Info del evento */}
      <section
        data-testid={ASIGNAR.info}
        className="rounded-2xl border hairline bg-card p-5 md:p-6 shadow-soft"
      >
        <h2 className="font-heading text-lg text-foreground">
          {contexto.nombreEvento}
        </h2>
        <ul className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm text-muted-foreground">
          <li className="flex items-center gap-2">
            <CalendarDays className="w-4 h-4 text-accent" strokeWidth={1.6} />
            <span>{formatearFechaLarga(contexto.fechaEvento)}</span>
          </li>
          <li className="flex items-center gap-2">
            <Clock className="w-4 h-4 text-accent" strokeWidth={1.6} />
            <span>{contexto.horaEvento}</span>
          </li>
          <li className="flex items-center gap-2">
            <MapPin className="w-4 h-4 text-accent" strokeWidth={1.6} />
            <span>{contexto.salon}</span>
          </li>
          {contexto.mesaLabel && (
            <li className="flex items-center gap-2">
              <Armchair className="w-4 h-4 text-accent" strokeWidth={1.6} />
              <span>{contexto.mesaLabel}</span>
            </li>
          )}
          <li className="flex items-center gap-2 sm:col-span-2">
            <Ticket className="w-4 h-4 text-accent" strokeWidth={1.6} />
            <span>
              {contexto.cantidad}{" "}
              {contexto.cantidad === 1 ? "boleto" : "boletos"}
            </span>
          </li>
        </ul>
      </section>

      {/* Resumen superior */}
      <section
        data-testid={ASIGNAR.resumen}
        className="rounded-2xl border hairline bg-card p-5 grid grid-cols-3 gap-3 text-center"
      >
        <div>
          <div
            data-testid={ASIGNAR.resumenTotal}
            className="font-heading text-2xl text-foreground"
          >
            {totales.total}
          </div>
          <div className="text-[11px] uppercase tracking-wider text-muted-foreground mt-1">
            {totales.total === 1 ? "boleto" : "boletos"}
          </div>
        </div>
        <div className="border-l border-r hairline">
          <div
            data-testid={ASIGNAR.resumenAsignados}
            className="font-heading text-2xl text-emerald-600 dark:text-emerald-400"
          >
            {totales.asignados}
          </div>
          <div className="text-[11px] uppercase tracking-wider text-muted-foreground mt-1">
            asignados
          </div>
        </div>
        <div>
          <div
            data-testid={ASIGNAR.resumenPendientes}
            className={cn(
              "font-heading text-2xl",
              totales.pendientes === 0
                ? "text-muted-foreground"
                : "text-primary dark:text-accent",
            )}
          >
            {totales.pendientes}
          </div>
          <div className="text-[11px] uppercase tracking-wider text-muted-foreground mt-1">
            pendientes
          </div>
        </div>
      </section>

      {nombresRepetidos.length > 0 && (
        <div
          data-testid={ASIGNAR.warnRepetidos}
          role="alert"
          className="rounded-2xl border hairline bg-amber-500/10 border-amber-500/40 p-4 flex items-start gap-3"
        >
          <AlertCircle
            className="w-4 h-4 text-amber-700 dark:text-amber-300 mt-0.5"
            strokeWidth={1.8}
          />
          <p className="text-sm text-foreground leading-relaxed">
            Antes del evento, cada boleto deberá estar asignado a un asistente
            diferente.
          </p>
        </div>
      )}

      {/* Lista compacta · sin expansión */}
      <ul className="rounded-2xl border hairline bg-card overflow-hidden divide-y divide-border">
        {boletos.map((b, i) => {
          const total = boletos.length;
          const porConfirmar = sinConfirmar.has(b.id);
          const validado = !porConfirmar && b.estado === "VALIDADO";
          const draft =
            validado || porConfirmar ? b.asistenteNombre || "" : drafts[b.id] ?? "";
          const asignado = draft.trim().length > 0;
          const dirty = dirtyIds.has(b.id);

          return (
            <li
              key={b.id}
              data-testid={ASIGNAR.item(i + 1)}
              className="px-5 py-4 flex flex-col gap-3"
            >
              <div className="flex items-center gap-3">
                <span className="w-9 h-9 shrink-0 grid place-items-center rounded-full bg-muted text-muted-foreground">
                  <User className="w-4 h-4" strokeWidth={1.6} />
                </span>
                <div className="flex-1 min-w-0">
                  <div className="text-[11px] uppercase tracking-wider text-muted-foreground">
                    Boleto {i + 1} de {total}
                    {modoEvento && b.salon ? ` · ${b.salon}` : ""}
                    {modoEvento && b.mesa != null ? ` · Mesa ${b.mesa}` : ""}
                  </div>
                  <div className="mt-0.5 text-sm">
                    {asignado ? (
                      <span
                        data-testid={ASIGNAR.itemNombre(i + 1)}
                        className="text-foreground truncate"
                      >
                        {draft.trim()}
                      </span>
                    ) : (
                      <span
                        data-testid={ASIGNAR.itemPendiente(i + 1)}
                        className="text-muted-foreground italic"
                      >
                        Pendiente de asignar
                      </span>
                    )}
                  </div>
                </div>
                {porConfirmar ? (
                  <span
                    data-testid={ASIGNAR.itemPorConfirmar(i + 1)}
                    className="shrink-0 text-[10px] uppercase tracking-wider text-amber-800 dark:text-amber-300 bg-amber-500/10 border border-amber-500/40 px-2 py-1 rounded-full"
                  >
                    Por confirmar
                  </span>
                ) : validado ? (
                  <span
                    data-testid={ASIGNAR.itemUtilizado(i + 1)}
                    className="shrink-0 text-[10px] uppercase tracking-wider text-emerald-700 dark:text-emerald-300 bg-emerald-500/10 border border-emerald-500/40 px-2 py-1 rounded-full"
                  >
                    Utilizado
                  </span>
                ) : (
                  dirty && (
                    <span
                      data-testid={ASIGNAR.itemSinGuardar(i + 1)}
                      className="shrink-0 text-[10px] uppercase tracking-wider text-primary dark:text-accent bg-primary/10 dark:bg-accent/10 px-2 py-1 rounded-full"
                    >
                      Sin guardar
                    </span>
                  )
                )}
              </div>

              {porConfirmar ? (
                <BoletoPorConfirmar
                  n={i + 1}
                  cargando={reintentando.has(b.id)}
                  onReintentar={() => reintentarBoleto(b.id)}
                />
              ) : validado ? (
                <div
                  data-testid={ASIGNAR.msgValidado(i + 1)}
                  className="rounded-xl border border-emerald-500/40 bg-emerald-500/10 p-3 text-xs text-foreground"
                >
                  Este boleto ya fue utilizado y no puede modificarse.
                </div>
              ) : (
                <div className="flex flex-col sm:flex-row gap-2 sm:items-center">
                  <div className="relative flex-1">
                    <User
                      className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground"
                      strokeWidth={1.6}
                    />
                    <input
                      type="text"
                      autoComplete="off"
                      data-testid={ASIGNAR.inputNombre(i + 1)}
                      value={draft}
                      onChange={(e) => setDraftFor(b.id, e.target.value)}
                      placeholder="Nombre y apellidos"
                      className="w-full pl-11 pr-4 py-2.5 rounded-xl bg-background border hairline text-foreground placeholder:text-muted-foreground/70 focus:outline-none focus:ring-2 focus:ring-accent/50 transition-all text-sm"
                    />
                  </div>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      data-testid={ASIGNAR.btnUsarMisDatos(i + 1)}
                      onClick={() => usarMiNombreEn(b.id)}
                      className="inline-flex items-center gap-2 rounded-full px-4 py-2 text-xs font-medium border hairline bg-card text-foreground hover:bg-muted transition-colors"
                    >
                      <UserCircle2 className="w-3.5 h-3.5" strokeWidth={1.7} />
                      Usar mi nombre
                    </button>
                    {(b.asistenteNombre || "").trim().length > 0 && (
                      <button
                        type="button"
                        data-testid={ASIGNAR.btnVerBoleto(i + 1)}
                        onClick={() =>
                          intentarSalir(`/app/boletos/${b.id}`, {
                            from: rutaActual,
                          })
                        }
                        className="inline-flex items-center gap-2 rounded-full px-4 py-2 text-xs font-medium border hairline bg-card text-foreground hover:bg-muted transition-colors"
                      >
                        <QrCode className="w-3.5 h-3.5" strokeWidth={1.7} />
                        Ver
                      </button>
                    )}
                  </div>
                </div>
              )}
            </li>
          );
        })}
      </ul>

      {/* Botón global "Guardar cambios" */}
      <div className="sticky bottom-4 sm:static sm:bottom-auto">
        <button
          type="button"
          data-testid={ASIGNAR.btnGuardarCambios}
          onClick={guardarCambios}
          disabled={!hayCambios || saving}
          className={cn(
            "w-full inline-flex items-center justify-center gap-2 rounded-full",
            "px-6 py-4 text-sm font-semibold uppercase tracking-wide",
            "bg-primary text-primary-foreground dark:bg-accent dark:text-accent-foreground",
            "ring-1 ring-primary/20 dark:ring-accent/40 shadow-soft",
            "transition-all duration-200 active:scale-[0.99] hover:opacity-95",
            "disabled:opacity-50 disabled:cursor-not-allowed",
          )}
        >
          <Save className="w-4 h-4" strokeWidth={1.7} />
          {saving
            ? "Guardando..."
            : hayCambios
              ? `Guardar cambios (${dirtyIds.size})`
              : "Sin cambios por guardar"}
        </button>
      </div>

      {/* Diálogo · Salir con cambios sin guardar */}
      {salirDialog && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-black/50 backdrop-blur-sm animate-fade-in"
            onClick={cancelarSalir}
          />
          <div
            data-testid={ASIGNAR.dialogSalirScreen}
            className="relative w-full max-w-md rounded-2xl border hairline bg-card p-6 md:p-8 shadow-elevated animate-fade-up"
          >
            <div className="mx-auto w-12 h-12 grid place-items-center rounded-2xl bg-amber-500/15 text-amber-700 dark:text-amber-300 mb-4">
              <AlertCircle className="w-5 h-5" strokeWidth={1.7} />
            </div>
            <h2 className="font-heading text-xl text-foreground text-center">
              Tienes cambios sin guardar
            </h2>
            <p className="mt-3 text-sm text-muted-foreground text-center leading-relaxed">
              Si sales ahora, los nombres editados no se guardarán. ¿Deseas
              continuar de todas formas?
            </p>
            <div className="mt-6 flex flex-col sm:flex-row gap-3">
              <button
                type="button"
                data-testid={ASIGNAR.dialogSalirCancelar}
                onClick={cancelarSalir}
                className="flex-1 rounded-full px-5 py-3 text-sm font-medium border hairline bg-card text-foreground hover:bg-muted transition-colors"
              >
                Seguir editando
              </button>
              <button
                type="button"
                data-testid={ASIGNAR.dialogSalirConfirmar}
                onClick={confirmarSalir}
                className="flex-1 rounded-full px-5 py-3 text-sm font-semibold bg-destructive text-destructive-foreground hover:opacity-90 transition-opacity"
              >
                Salir sin guardar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
